import React, { useState, useEffect, useRef } from 'react';
import VideoPlayer from '../components/VideoPlayer';
import ChatInterface from '../components/ChatInterface';
import { interactionManager } from '../interactionLogic';
import '../App.css';

const SELECTED_CHARACTER = "missy";
var AI_CHARACTER_NAME = "AI"
export const API_BASE_URL = process.env.REACT_APP_API_URL;

const Character = () => {
    const [messages, setMessages] = useState([]);
    const [currentVideo, setCurrentVideo] = useState(null);
    const [nextVideo, setNextVideo] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
    const [currentScore, setCurrentScore] = useState(0);

    const queueRef = useRef([]);
    const didInitialize = useRef(false);
    const isPlayModeRef = useRef(false);
    const inactivityTime = useRef(0);
    const preloadedUrls = useRef(new Map()); 

    const MAX_INACTIVE_TIME = 60;

    // Helper: Add to queue and trigger preload immediately
    const addToQueue = (actions, clearOld = false) => {
        console.log("Added to Queue\n\n", JSON.stringify(actions))
        if (clearOld) {
            queueRef.current = [...actions];
        } else {
            queueRef.current.push(...actions);
        }
        console.log("Queue Updated:", queueRef.current);
        preloadQueue();
    };

    // Helper: Fetch Signed URL
    const fetchVideoUrl = async (filename) => {
        if (preloadedUrls.current.has(filename)) return preloadedUrls.current.get(filename);
        try {
            const res = await fetch(`${API_BASE_URL}/api/files/video/${SELECTED_CHARACTER}/${filename}`);
            const data = await res.json();
            preloadedUrls.current.set(filename, data.url);
            return data.url;
        } catch (e) {
            console.error("Fetch URL error", e);
            return null;
        }
    };

    const preloadQueue = async () => {
        // Find all videos in queue that haven't started preloading
        const videoActions = queueRef.current.filter(action => action.type === 'video' && !action.preloadingStarted);
        
        videoActions.forEach(async (action) => {
            action.preloadingStarted = true;
            const filename = action.data.filename;
            
            try {
                const url = await fetchVideoUrl(filename);
                if (!url) throw new Error("No URL");

                const tempVideo = document.createElement('video');
                tempVideo.preload = 'auto';
                tempVideo.src = url;

                tempVideo.onerror = () => {
                    console.error(`Preload failed: ${filename}. Removing from queue.`);
                    queueRef.current = queueRef.current.filter(a => a.data?.filename !== filename);
                };

                tempVideo.oncanplaythrough = () => {
                    console.log(`Preloaded & Cached: ${filename}`);
                    tempVideo.remove();
                };
            } catch (e) {
                console.error("Preload execution error", e);
            }
        });
    };

    const initialize = async () => {
        const success = await interactionManager.initialize(SELECTED_CHARACTER);
        if (success) {
            AI_CHARACTER_NAME = interactionManager.characterConfig.name;
            const initialVideo = interactionManager.getInitialVideo();
            addToQueue([{ type: 'video', data: initialVideo }]);
            processNextInQueue();
            setIsInitialized(true);
        }
    };

    useEffect(() => {
        if (didInitialize.current) return;
        didInitialize.current = true;
        initialize();
        const timer = setInterval(() => { inactivityTime.current += 1; }, 1000);
        return () => clearInterval(timer);
    }, []);

    const processNextInQueue = async () => {
        console.log("processNextInQueue")
        if (queueRef.current.length === 0) return;
        
        const nextAction = queueRef.current.shift();
        console.log("action", nextAction)

        if (nextAction.type === 'video') {
            disableLastWaitingMessage();
            const url = await fetchVideoUrl(nextAction.data.filename);
            setCurrentVideo({ ...nextAction.data, url, _timestamp: Date.now() });

            prepareNextBuffer();
        } else {
            setIsAwaitingResponse(false);         
            var msgData = {}
            if (nextAction.type === 'message'){
                msgData = { type: "normal", text: nextAction.data.text }
            }else if (nextAction.type === 'qna_message'){
                msgData = { type: "qna", ...nextAction.data }
                setCurrentScore(interactionManager.currentScore);
            }

            setTimeout(() => {
                setMessages(prev => [...prev, { ...msgData, from: AI_CHARACTER_NAME, timestamp: new Date() }]);
            }, 1000);
            processNextInQueue(); // Continue processing

            prepareNextBuffer();
        }
    };

    const prepareNextBuffer = async () => {
        const nextVideoAction = queueRef.current.find(a => a.type === 'video');
        if (nextVideoAction) {
            console.log("Prepare Next Queue.", nextVideoAction.data.filename);
            const url = await fetchVideoUrl(nextVideoAction.data.filename);
            setNextVideo({ ...nextVideoAction.data, url });
        }
    };

    const handleVideoEnd = () => {
        console.log("Video Ended. Popping from queue.");
        processNextInQueue(); // Trigger the next action immediately
    };

    const prepareNextVideo = () => {
        // Logic to fill queue if empty
        if (queueRef.current.length <= 1) {
            let category = inactivityTime.current > MAX_INACTIVE_TIME 
                ? interactionManager.characterConfig.defaults.idle 
                : interactionManager.characterConfig.defaults.listening;
            
            let video = interactionManager.findCategoryVideo(category);
            addToQueue([{ type: 'video', data: video }]);
            setIsAwaitingResponse(false); 
        }
        prepareNextBuffer(); // Update the nextVideo state for the player
    };

    const disableLastWaitingMessage = () => {
        setMessages(prev => {
            if (prev.length === 0 || prev[prev.length - 1].from !== 'user') return prev;
            const last = { ...prev[prev.length - 1], showWaitingMessage: false };
            return [...prev.slice(0, -1), last];
        });
    };

    const sendMessage = async (data) => {
        if (!isInitialized) return;
        inactivityTime.current = 0;

        if (data.type === "USER_SEND_MESSAGE") {
            if (isAwaitingResponse) return;
            setMessages(prev => [...prev, { from: 'user', text: data.text, timestamp: new Date(), showWaitingMessage: true }]);
            setIsAwaitingResponse(true);

            let newActions = isPlayModeRef.current 
                ? await interactionManager.processUserGameQuery(data.text)
                : await interactionManager.processUserChatQuery(data.text);

            addToQueue(newActions, true); // Replace queue with new response sequence
            // processNextInQueue();
        } else if (data.type === "TRANSITION_ACTION") {
            const newActions = interactionManager.processUserActionSelection(data.id);
            addToQueue(newActions);
            setIsAwaitingResponse(true);
            // processNextInQueue();
        }
    };

    if (!isInitialized) return <div className="loading-screen">Loading Character...</div>;

    return (
        <div className="app-container">
            <div className="page-section">
                <VideoPlayer
                    videoInfo={currentVideo}
                    nextVideoInfo={nextVideo}
                    requestNextVideo={prepareNextVideo}
                    onVideoEnd={handleVideoEnd}
                />
                <ChatInterface
                    messages={messages}
                    sendMessage={sendMessage}
                    characterName={AI_CHARACTER_NAME}
                    isSending={isAwaitingResponse}
                    isPlayModeRef={isPlayModeRef}
                    currentScore={currentScore}
                    exclusive_actions={interactionManager.exclusive_actions}
                />
            </div>
        </div>
    );
};

export default Character;